import React, { useContext } from 'react';
import { styled, alpha } from '@mui/material/styles';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoTooltip from 'src/components/UI/addons/InfoTooltip';
import { ThemeContext } from 'src/store/ContextStore';
import { toOutlineIcon } from './sidebarIconRefresh';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import {
  PEDIGREE_RECORD_SURFACES,
  SURFACE_GREY,
  FIELD_BORDER,
  READONLY_FILL
} from './sections/clinical-notes-section/recordCardStyles';

// Pedigree sidebar accordion refresh. Default OFF: with the flag off, both
// pedigree sidebars (clinician `pedigree-chart/` and legacy patient `pedigree/`)
// render pixel-for-pixel as they do today. With it on, the accordion gets the
// Option 2 "Active accent" treatment: quiet mid-grey outline icons and stronger
// navy uppercase labels at rest, and a teal active cue (icon, chevron, faint
// tint and a 3px inset left bar) on the expanded row.
//
// Rolled ON 2026-07-11 (Tim). Once the refresh is confirmed stable in the app,
// REMOVE this flag and every `refresh` branch below (and the LEGACY_* style
// objects) rather than leaving a long-lived dead style branch.
export const PEDIGREE_SIDEBAR_REFRESH = true;

// Visual-intent colours that have no existing theme token (audited). The teal
// active cue uses the real `theme.accent` token (default #2ba9bf), passed in as
// `accent`; only these greys/navy are literals.
const REFRESH = {
  icon: '#8994a1', // mid slate grey, clearly darker than white so it never reads as disabled
  label: '#2b3648', // navy, resting label
  // Darkened, AA-safe teal for the active label only. The bright brand teal
  // (theme.accent) fails the AA text-contrast floor on white, so small text
  // uses this darkened value while the larger glyphs keep the brand teal.
  labelActive: '#0e7186',
  hairline: '#eef1f4', // row divider
  // TGCLOUD-1669 — the collapsed-section summary line. Both clear WCAG AA as
  // small text on white AND on the faint teal tint an expanded/hovered row
  // carries, which MUI's alpha-based `text.secondary` does not reliably do.
  summary: '#55606d', // 6.4:1 on white
  summaryNone: '#5b6673' // 5.8:1 on white, italic "none recorded"
};

// Re-exported for the Add Relative panel, whose tiles deliberately wear the SAME
// resting/hover treatment as these accordion rows: neutral at rest with the navy
// label, a faint accent tint on hover with the brand teal on the glyph and the
// AA-safe darkened teal on the small label. One definition, so the panel cannot
// drift from the row above it.
export const REFRESH_COLOURS = REFRESH;

const withoutRefreshProps = (prop) => prop !== 'refresh' && prop !== 'accent';

// Legacy (flag-off) styles are the CURRENT definitions verbatim, so flag-off is
// a pixel-for-pixel match to today. The refresh branch is a clean, independent
// object (not a spread over the legacy one) to avoid the legacy nested
// selectors bleeding through.
const LEGACY_ACCORDION = {
  border: '1px solid rgba(0, 0, 0, .125)',
  boxShadow: 'none',
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  },
  '&.Mui-expanded': {
    margin: 'auto'
  }
};

// Refresh: no box outline, just a single hairline divider beneath each row and
// none under the last row.
const REFRESH_ACCORDION = {
  border: 'none',
  borderBottom: `1px solid ${REFRESH.hairline}`,
  boxShadow: 'none',
  '&:before': {
    display: 'none'
  },
  '&.Mui-expanded': {
    margin: 'auto'
  },
  '&:last-of-type': {
    borderBottom: 'none'
  }
};

const Accordion = styled(MuiAccordion, {
  shouldForwardProp: withoutRefreshProps
})(({ refresh }) => (refresh ? REFRESH_ACCORDION : LEGACY_ACCORDION));

const LEGACY_SUMMARY = {
  backgroundColor: 'rgba(0, 0, 0, .03)',
  borderBottom: '1px solid rgba(0, 0, 0, .125)',
  marginBottom: -1,
  minHeight: 56,
  '&.Mui-expanded': {
    minHeight: 56
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: '12px 0'
  }
};

const AccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: withoutRefreshProps
})(({ theme, refresh, accent }) => {
  if (!refresh) return LEGACY_SUMMARY;
  const teal = accent || '#2ba9bf';
  return {
    // Refresh: white resting row; quiet mid-grey icon, chevron and a navy
    // uppercase label. The divider lives on the Accordion root, so the summary
    // carries no border. Colours/background only, so nothing reflows.
    backgroundColor: theme.palette.background.paper,
    minHeight: 56,
    transition: 'background-color .15s ease',
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: '12px 0'
    },
    // TGCLOUD-1669 — the summary line truncates with an ellipsis, and
    // `text-overflow` only takes effect once every flex/grid ancestor between
    // the text and the panel can actually shrink. MUI's summary content is a
    // flex item whose default `min-width: auto` refuses to, so without this the
    // line paints straight through the drawer edge and is chopped by an
    // ancestor's overflow with no ellipsis, which reads as a rendering bug.
    '& .MuiAccordionSummary-content': {
      minWidth: 0
    },
    '& .MuiIconButton-root': { color: REFRESH.icon },
    '& .MuiAccordionSummary-expandIconWrapper': { color: REFRESH.icon },
    '& .sidebar-refresh-label': {
      color: REFRESH.label,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em'
    },
    // Hover shows the same faint accent tint as the expanded (active) row.
    // Kept before the expanded rule so that, on the equal-specificity tie, the
    // expanded rule (which also draws the left bar) wins when an expanded row is
    // hovered.
    '&:hover': {
      backgroundColor: alpha(teal, 0.07)
    },
    // Expanded (active): faint teal tint plus a 3px teal left bar drawn with an
    // inset box-shadow (not a border) so nothing reflows. Icon and chevron go
    // teal; the chevron keeps MUI's default 180deg rotation. The label goes to
    // the AA-safe darkened teal.
    '&.Mui-expanded': {
      minHeight: 56,
      backgroundColor: alpha(teal, 0.07),
      boxShadow: `inset 3px 0 0 ${teal}`,
      '& .MuiIconButton-root': { color: teal },
      '& .MuiAccordionSummary-expandIconWrapper': { color: teal },
      '& .sidebar-refresh-label': { color: REFRESH.labelActive }
    }
  };
});

const AccordionDetails = styled(MuiAccordionDetails, {
  shouldForwardProp: (prop) => prop !== 'surfaces'
})(({ theme, surfaces }) => ({
  padding: theme.spacing(2),
  // Record-surfaces refresh: the content region goes a very light grey so white
  // editable fields lift off it, while read-only (disabled) fields stay a
  // distinct darker grey. Colours only, so nothing reflows. The resting field
  // border is set at low specificity so MUI's focus/hover/error borders still
  // win. Gated by PEDIGREE_RECORD_SURFACES.
  ...(surfaces && {
    backgroundColor: SURFACE_GREY,
    '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
    '& .MuiOutlinedInput-root.Mui-disabled': { backgroundColor: READONLY_FILL },
    '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
      borderColor: FIELD_BORDER
    },
    // The MUI X v8 DatePicker (Date of Birth / Date of Death) renders its own
    // PickersOutlinedInput, NOT a plain OutlinedInput — so the rules above miss
    // it and the field stayed transparent over the grey surface, reading as
    // disabled when it is not. Mirror the same white-lift / read-only fill /
    // border onto the pickers input classes.
    '& .MuiPickersOutlinedInput-root': { backgroundColor: '#fff' },
    '& .MuiPickersOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
    '& .MuiPickersOutlinedInput-root.Mui-disabled': {
      backgroundColor: READONLY_FILL
    },
    '& .MuiPickersOutlinedInput-root.Mui-disabled .MuiPickersOutlinedInput-notchedOutline':
      {
        borderColor: FIELD_BORDER
      }
  })
}));

// A single sidebar accordion row. Presentational only: it renders exactly the
// markup both PedigreeSideBar trees rendered inline, gated by `refresh`. When
// `refresh` is false the DOM is identical to today's row.
export default function SidebarSection({
  item,
  expanded,
  onChange,
  summaryRef,
  dirty = false,
  // TGCLOUD-1669 — the collapsed-section summary. Three-valued on purpose, and
  // the third value is the point (see sectionSummary.js):
  //   string    → render it
  //   null      → render "none recorded" in italic
  //   undefined → render NOTHING; the section has not reported yet
  // Collapsing undefined into null would make a patient with twelve risk
  // scores read as having none for as long as the section was still loading.
  summary,
  refresh = PEDIGREE_SIDEBAR_REFRESH,
  surfaces = PEDIGREE_RECORD_SURFACES,
  children
}) {
  const t = useAutoTranslation();
  const { theme } = useContext(ThemeContext) || {};
  const accent = theme?.accent || '#2ba9bf';
  // The section names are this app's own (getSideBarItems); summaries arrive
  // translated (buildSectionSummaries takes t).
  const label = t(item.label);
  const iconEl = refresh ? toOutlineIcon(item.icon) : item.icon;
  const hasSummary = summary !== undefined;
  const isNone = summary === null;

  return (
    <Accordion square expanded={expanded} onChange={onChange} refresh={refresh}>
      <AccordionSummary
        aria-controls={item.value}
        id={item.value}
        // Stable Copilot walkthrough anchor, derived per section. Only anchors
        // listed in the vocab (e.g. pedigree.sidebar.addRelative) are referenced
        // by walkthroughs; the rest are inert until a future tour needs them.
        data-va-anchor={`pedigree.sidebar.${item.value}`}
        expandIcon={<ArrowDropDownIcon />}
        ref={summaryRef}
        refresh={refresh}
        accent={accent}
      >
        {/* Grid rather than a flex row so the icon can span both lines and stay
            centred against them without a hard-coded indent for the summary.
            With no summary it collapses to a single row and lays out exactly as
            the old flex row did. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'center',
            columnGap: 1,
            minWidth: 0,
            width: '100%'
          }}
        >
          {/* Decorative only (the whole AccordionSummary is the button). Render
              as a span so it isn't a <button> nested inside the summary's
              <button> — invalid DOM that React 19 flags as an error. */}
          <IconButton
            color={refresh ? 'default' : 'primary'}
            component="span"
            disableRipple
            sx={hasSummary ? { gridRow: 'span 2' } : undefined}
          >
            {iconEl}
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              minWidth: 0
            }}
          >
            <Typography
              className={refresh ? 'sidebar-refresh-label' : undefined}
              sx={{
                pt: 0.1
              }}
            >
              {label}
            </Typography>
            {item?.info && (
              <InfoTooltip label={t(item.info)} fontSize={'5px'} help />
            )}
            {dirty && (
              <Box
                component="span"
                title={t('Unsaved changes')}
                aria-label={t('Unsaved changes in {{section}}', {
                  section: label
                })}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#BA7517',
                  ml: 0.5,
                  flexShrink: 0
                }}
              />
            )}
          </Box>
          {hasSummary && (
            <Typography
              // A block box: `text-overflow` is ignored on a non-replaced
              // INLINE box, so a span here would paint through the drawer edge
              // and be chopped with no ellipsis.
              component="div"
              className="sidebar-section-summary"
              // The full value on hover, so truncation never hides information.
              // Nothing to reveal for "none recorded", so no title there.
              title={isNone ? undefined : summary}
              sx={{
                gridColumn: 2,
                minWidth: 0,
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: '0.78rem',
                lineHeight: 1.4,
                mt: '1px',
                color: isNone ? REFRESH.summaryNone : REFRESH.summary,
                fontStyle: isNone ? 'italic' : 'normal'
              }}
            >
              {isNone ? t('none recorded') : summary}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails surfaces={surfaces}>{children}</AccordionDetails>
    </Accordion>
  );
}
